import type { AppLocale } from '@/constants/appLocale'

const DEFAULT_COUPON_LIST_IMAGE_FILE = '2b39f5168b82ea35851c5e1b78e173b2.png'

const COUPON_DETAIL_ZH_IMAGE_FILE_1 = 'd8eff4a05e585d8da9f549187dc6278b.png'
const COUPON_DETAIL_EN_IMAGE_FILE_1 = '3e34d15bf3e73239ea0cd1af5b02568e.png'

const COUPON_DETAIL_ZH_IMAGE_FILE_2 = 'f6f1bdc4d17cb8bdfaca803f80be20a5.png'
const COUPON_DETAIL_EN_IMAGE_FILE_2 = '76c4e6940b6b6f9c839c06d16b45d4bb.png'

const COUPON_DETAIL_ZH_IMAGE_FILE_3 = 'a89459c6ffc8dfeb105a1e4f05bf1d6f.png'
const COUPON_DETAIL_EN_IMAGE_FILE_3 = '30fd26587c118b4eb69c9508d8af4dc8.png'

export type CouponLocaleText = Record<AppLocale, string>

const localeText = (
  zhCN: string,
  zhTW: string,
  enUS: string,
  jaJP: string,
  koKR: string,
): CouponLocaleText => ({
  zh_CN: zhCN,
  zh_TW: zhTW,
  en_US: enUS,
  ja_JP: jaJP,
  ko_KR: koKR,
})

export const COUPON_STATIC_TEXT = {
  touristExclusiveSuffix: localeText(
    '游客专属优惠券',
    '遊客專屬優惠券',
    ' Tourist Exclusive Coupon',
    '観光客専用クーポン',
    ' 관광객 전용 쿠폰',
  ),
  claimToday: localeText(
    '现在领取，当日使用',
    '現在領取，當日使用',
    'Claim now, use today',
    '今すぐ受け取り、当日利用',
    '지금 받고 당일 사용',
  ),
  participantsSuffix: localeText(
    '人参加过',
    '人參加過',
    'people joined',
    '人が参加済み',
    '명 참여',
  ),
} as const

export const COUPON_CATEGORY = {
  shopping: localeText('购物', '購物', 'Shopping', 'ショッピング', '쇼핑'),
  fashion: localeText('时尚服装', '時尚服裝', 'Fashion', 'ファッション', '패션'),
  beauty: localeText('美容护肤', '美容護膚', 'Beauty', '美容', '뷰티'),
  dutyFree: localeText('免税店', '免稅店', 'Duty-free', '免税店', '면세점'),
  jewelry: localeText('珠宝腕表', '珠寶腕錶', 'Jewelry & Watches', 'ジュエリー・時計', '주얼리/시계'),
  departmentStore: localeText('高端百货', '高端百貨', 'Department Store', '百貨店', '백화점'),
  optical: localeText('眼镜店', '眼鏡店', 'Optical', '眼鏡店', '안경점'),
  vintage: localeText('二手店', '二手店', 'Vintage', '中古店', '빈티지'),
  outdoor: localeText('运动户外', '運動戶外', 'Outdoor', 'アウトドア', '아웃도어'),
  other: localeText('其他', '其他', 'Other', 'その他', '기타'),
  electronics: localeText('数码家电', '數碼家電', 'Electronics', '家電', '디지털/가전'),
  homeGoods: localeText('家居用品', '家居用品', 'Home Goods', '生活雑貨', '생활용품'),
  drugstore: localeText('药妆店', '藥妝店', 'Drugstore', 'ドラッグストア', '드럭스토어'),
} as const

export type TempCouponStore = {
  id: string
  merchantName: string
  discount?: CouponLocaleText
  primaryCategory: CouponLocaleText
  secondaryCategory: CouponLocaleText
  rating: string
  reviewCount: number
  participantCount: string
  /** 列表卡片图片 */
  listImageFile: string
  /** 详情页中文图片 */
  detailImageZhFile: string
  /** 详情页英文图片，非中文语言都使用这个字段 */
  detailImageEnFile: string
}

const withStoreIds = (stores: Array<Omit<TempCouponStore, 'id'>>): TempCouponStore[] =>
  stores.map((store, index) => ({
    id: `store-${index + 1}`,
    ...store,
  }))

export const TEMP_COUPON_STORES: TempCouponStore[] = withStoreIds([
  {
    merchantName: 'SNIDEL',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.8',
    reviewCount: 21,
    participantCount: '300+',
    listImageFile: '5dab7e42c569baf9304d521dc414f2ed.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'SNIDEL HOME',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.6',
    reviewCount: 32,
    participantCount: '200+',
    listImageFile: '5cc9dd2a5e7d8fa0b57093e10c0c1656.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'gelato pique',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.9',
    reviewCount: 65,
    participantCount: '500+',
    listImageFile: '9e64d080087d25ebfac381ca51e1b685.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'LILY BROWN',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.6',
    reviewCount: 84,
    participantCount: '300+',
    listImageFile: '7e9b084dc49cfa6afe370f0b8e3eb830.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'FRAY I.D',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.4',
    reviewCount: 28,
    participantCount: '200+',
    listImageFile: 'f43fb98fd4d583e443bc6ff1ad55d666.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'Mila Owen',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.7',
    reviewCount: 56,
    participantCount: '400+',
    listImageFile: '324ec7da6d41469d92a7ff29121a8173.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'FURFUR',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.6',
    reviewCount: 55,
    participantCount: '300+',
    listImageFile: '7b2997150b7f443ebeb5fae4168c44a8.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'styling',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.5',
    reviewCount: 121,
    participantCount: '600+',
    listImageFile: 'ddafef2f4fcb024be16f3ef26f31b13d.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'CELFORD',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.8',
    reviewCount: 89,
    participantCount: '500+',
    listImageFile: '6353dcafe8edcca82d31258955e80492.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'USAGI ONLINE STORE',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.5',
    reviewCount: 48,
    participantCount: '100+',
    listImageFile: '23dc72d80170eb426731de12ec553a47.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'emmi',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.9',
    reviewCount: 110,
    participantCount: '600+',
    listImageFile: 'ba69c480520d84c3d1c75520dd3702f0.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'MIESROHE',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.5',
    reviewCount: 31,
    participantCount: '300+',
    listImageFile: 'c3cad77a36b3464ed933cffa4ab11352.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'MUCHA',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.9',
    reviewCount: 128,
    participantCount: '400+',
    listImageFile: '185bf6c3639ef8d519a6b7050f1987e9.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'gelato pique homme',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.4',
    reviewCount: 31,
    participantCount: '100+',
    listImageFile: '4abe2cab3ad898ee0f49ccdc28fafad0.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'JAMIE KAY',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.6',
    reviewCount: 66,
    participantCount: '200+',
    listImageFile: '8a3952d3663ebbfa6880c44541ce7e20.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'SOFTHYPHEN',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.8',
    reviewCount: 36,
    participantCount: '200+',
    listImageFile: '4d378f0e6f3687a7ec1c25bdeba641a7.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_1,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_1,
  },
  {
    merchantName: 'CosmeKitchen',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.beauty,
    rating: '4.9',
    reviewCount: 36,
    participantCount: '400+',
    listImageFile: '0f2abd6b6ba2b09a950ab5d2b7caca4f.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_2,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_2,
  },
  {
    merchantName: 'Biople',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.beauty,
    rating: '4.6',
    reviewCount: 45,
    participantCount: '300+',
    listImageFile: '37b0b4f4474383384c742a2bac80eeee.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_2,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_2,
  },
  {
    merchantName: 'SNIDEL Beauty',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.beauty,
    rating: '4.8',
    reviewCount: 24,
    participantCount: '200+',
    listImageFile: '4e059b7c2e937feb2456e465280590b4.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_2,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_2,
  },
  {
    merchantName: 'Celvoke',
    discount: localeText(
      '94折+免税劵',
      '94折+免稅劵',
      '6% Off + Tax-free',
      '6% Off + 免税劵',
      '6% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.beauty,
    rating: '4.9',
    reviewCount: 106,
    participantCount: '500+',
    listImageFile: '27d1c9a4ac8d30dc2671c9ca04d15607.png',
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_2,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_2,
  },
  {
    merchantName: 'Anna Sui mini',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.5',
    reviewCount: 51,
    participantCount: '400+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'Flower Kids Factory',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.6',
    reviewCount: 42,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'kate spade',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.7',
    reviewCount: 61,
    participantCount: '400+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'Lovetoxic',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.6',
    reviewCount: 47,
    participantCount: '100+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'mezzo piano',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.9',
    reviewCount: 36,
    participantCount: '300+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'petit main',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.5',
    reviewCount: 28,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'minimal',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.5',
    reviewCount: 31,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'niko and...',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.8',
    reviewCount: 52,
    participantCount: '300+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'RAGE BLUE',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.4',
    reviewCount: 33,
    participantCount: '100+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'HARE',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.7',
    reviewCount: 41,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: COUPON_DETAIL_ZH_IMAGE_FILE_3,
    detailImageEnFile: COUPON_DETAIL_EN_IMAGE_FILE_3,
  },
  {
    merchantName: 'Japan Duty Free GINZA',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.dutyFree,
    rating: '4.5',
    reviewCount: 52,
    participantCount: '400+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '63194433111be46256dfc1735740ec2a.png',
    detailImageEnFile: '37ca68d0f50bad762099d701328be07b.png',
  },
  {
    merchantName: '萧邦Chopard',
    discount: localeText(
      '2.5% Off + 免税劵',
      '2.5% Off + 免稅劵',
      '2.5% Off + Tax-free',
      '2.5% Off + 免税劵',
      '2.5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.jewelry,
    rating: '4.7',
    reviewCount: 34,
    participantCount: '200+',
    listImageFile: '816adc269500cee5ff630ce5f659054d.png',
    detailImageZhFile: '89bd48e0047f719ce6a0eaa59a6733c6.png',
    detailImageEnFile: 'ca3b858f236106d3e140098a7fc8e99f.png',
  },
  {
    merchantName: 'Fukuya',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.departmentStore,
    rating: '4.5',
    reviewCount: 19,
    participantCount: '100+',
    listImageFile: '5c92776a19563aaf83d68c6df404dbe0.png',
    detailImageZhFile: 'ced8744204907440788e26fc07c0aef5.png',
    detailImageEnFile: 'a13b7d868279dd15c35f2ddb84040744.png',
  },
  {
    merchantName: '和光',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.departmentStore,
    rating: '4.9',
    reviewCount: 68,
    participantCount: '600+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '400a79d73f15f5b52cfbdc01ee05dc4c.png',
    detailImageEnFile: '492d497d1f179c0ddb13a0f50e5e0196.png',
  },
  {
    merchantName: 'PARIS MIKI',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.optical,
    rating: '4.5',
    reviewCount: 45,
    participantCount: '400+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '493188fb9a44da096098619177b07c30.png',
    detailImageEnFile: '36a11fbf1ec40a4c4ce6f0214ba25310.png',
  },
  {
    merchantName: 'megane super',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.optical,
    rating: '4.6',
    reviewCount: 38,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '19babbe516a2e9c16c3d5357aec18006.png',
    detailImageEnFile: '80a002aeaeff1bf5ca8e6fab03e8f02e.png',
  },
  {
    merchantName: 'HIROTAKA',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.jewelry,
    rating: '4.5',
    reviewCount: 26,
    participantCount: '100+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '5227f036d2c03577a22eb63dd1257830.png',
    detailImageEnFile: '8e1a5a4c64c5de72f337496711dc8af8.png',
  },
  {
    merchantName: 'CARA',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.vintage,
    rating: '4.4',
    reviewCount: 27,
    participantCount: '100+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: 'e101714ceb1c40748eb63da494a5769c.png',
    detailImageEnFile: 'a8866e8656e2cdfe7cc89639b1266eb5.png',
  },
  {
    merchantName: 'take-up',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.jewelry,
    rating: '4.6',
    reviewCount: 58,
    participantCount: '300+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: 'e6d6ff9cc8286439c8cef01048cd0a61.png',
    detailImageEnFile: 'd3dcccbbf0fb1a0f9280218779bcb563.png',
  },
  {
    merchantName: 'QOO VINTAGE',
    discount: localeText(
      '立减3000日元+免税劵',
      '立減3000日元+免稅劵',
      '3000 yen off + Tax-free',
      '3000 yen off + 免税劵',
      '3000 yen off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.vintage,
    rating: '4.5',
    reviewCount: 32,
    participantCount: '200+',
    listImageFile: '69fa806edc6e61093652dfeed1031231.png',
    detailImageZhFile: '7b98f5ca30d6f93b5460ee040d075a8d.png',
    detailImageEnFile: '26829e3b4253a822b544fe35c48a6881.png',
  },
  {
    merchantName: 'A&F',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.outdoor,
    rating: '4.9',
    reviewCount: 65,
    participantCount: '500+',
    listImageFile: 'f020d351c4a1590a456aad191189d2e1.png',
    detailImageZhFile: '23ec69111bd8b5e88ceedfda12bf18e7.png',
    detailImageEnFile: '4b3cce51e338f457b46f5906e75e9722.png',
  },
  {
    merchantName: 'GINZA TAILOR',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.7',
    reviewCount: 16,
    participantCount: '100+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '2422df33b1160fdb59f0f8b690e3372b.png',
    detailImageEnFile: '5fb05bc47fc5fdda2d7c34331b592314.png',
  },
  {
    merchantName: 'AMPLEUR',
    discount: localeText(
      '9折+免税劵',
      '9折+免稅劵',
      '10% Off + Tax-free',
      '10% Off + 免税劵',
      '10% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.beauty,
    rating: '4.5',
    reviewCount: 44,
    participantCount: '200+',
    listImageFile: 'fb4b281a4a4b93aac7a1760f348cf3d7.png',
    detailImageZhFile: 'f227de23efda14b4c5e4b3dd325bac08.png',
    detailImageEnFile: '3f325a93ed0d35687826de2b84dd0799.png',
  },
  {
    merchantName: '中川政七商店',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.other,
    rating: '4.4',
    reviewCount: 16,
    participantCount: '100+',
    listImageFile: '01716f92d9a21ca904a627f5fe5079e1.png',
    detailImageZhFile: '0d30c847c3e0777d5e071c75f0f950e1.png',
    detailImageEnFile: '5e9e7b36677b7823fc22c3b9b91ecbcb.png',
  },
  {
    merchantName: 'Big Camera',
    discount: localeText(
      '93折+免税劵',
      '93折+免稅劵',
      '7% Off + Tax-free',
      '7% Off + 免税劵',
      '7% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.electronics,
    rating: '4.9',
    reviewCount: 121,
    participantCount: '500+',
    listImageFile: '1b13febcc41cba403065f230c1c00218.png',
    detailImageZhFile: '90d5835a77d998f5e0e0f980e0f2f530.png',
    detailImageEnFile: '523ecb8e2d917a498cdcacb59b05ad46.png',
  },
  {
    merchantName: 'ORIHICA',
    discount: localeText(
      '9折+免税劵',
      '9折+免稅劵',
      '10% Off + Tax-free',
      '10% Off + 免税劵',
      '10% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.fashion,
    rating: '4.7',
    reviewCount: 66,
    participantCount: '300+',
    listImageFile: 'fb4b281a4a4b93aac7a1760f348cf3d7.png',
    detailImageZhFile: '26e9e2feaf26a96d3544abc206c5bf0b.png',
    detailImageEnFile: 'd33f3f664b569bb1630b4e34a4229c85.png',
  },
  {
    merchantName: 'SIXTH NIGHT第六夜',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.jewelry,
    rating: '4.5',
    reviewCount: 56,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '1c9387f6fa1b310d123b546a765abc9f.png',
    detailImageEnFile: '6c4529499e60f3e0c957d2e3c491eaef.png',
  },
  {
    merchantName: '旅皿',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.homeGoods,
    rating: '4.8',
    reviewCount: 72,
    participantCount: '300+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '5d0b55c12cace5c2e00038048f4dd0cc.png',
    detailImageEnFile: '109934c32ca26c925e7a4278430b0a71.png',
  },
  {
    merchantName: '堂吉柯德',
    discount: localeText(
      '93折+免税劵',
      '93折+免稅劵',
      '7% Off + Tax-free',
      '7% Off + 免税劵',
      '7% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.electronics,
    rating: '4.9',
    reviewCount: 131,
    participantCount: '600+',
    listImageFile: '26196276c02441ad77136eab282485fd.png',
    detailImageZhFile: 'b2f46f1353d984bed35d0ea661a69d2f.png',
    detailImageEnFile: '4fb3d230024cc791b92ce8c19f0649f8.png',
  },
  {
    merchantName: 'Cocokarafine',
    discount: localeText(
      '93折+免税劵',
      '93折+免稅劵',
      '7% Off + Tax-free',
      '7% Off + 免税劵',
      '7% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.drugstore,
    rating: '4.5',
    reviewCount: 45,
    participantCount: '100+',
    listImageFile: '873546f1abc658b7f4b3865522662a3f.png',
    detailImageZhFile: 'a1dcf010e2c6ba117c177bf4b3292a0e.png',
    detailImageEnFile: '616c59d7c032550b6e523a8119b527e8.png',
  },
  {
    merchantName: '松本清',
    discount: localeText(
      '93折+免税劵',
      '93折+免稅劵',
      '7% Off + Tax-free',
      '7% Off + 免税劵',
      '7% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.drugstore,
    rating: '4.5',
    reviewCount: 25,
    participantCount: '200+',
    listImageFile: '187343304c71f3e8949d24b2a374ffcc.png',
    detailImageZhFile: '7e7ff42bf06552dfb73d103cd3b22f77.png',
    detailImageEnFile: '96d80f449049675d764275fdbba9ab16.png',
  },
  {
    merchantName: 'CocochiCosme',
    discount: localeText(
      '9折+免税劵',
      '9折+免稅劵',
      '10% Off + Tax-free',
      '10% Off + 免税劵',
      '10% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.beauty,
    rating: '4.7',
    reviewCount: 65,
    participantCount: '300+',
    listImageFile: 'fb4b281a4a4b93aac7a1760f348cf3d7.png',
    detailImageZhFile: 'd9b504cc3632494b528d3b2efd7c63bd.png',
    detailImageEnFile: '5c232cd2c546a90ec38777b3483db410.png',
  },
  {
    merchantName: 'SJX',
    discount: localeText(
      '95折+免税劵',
      '95折+免稅劵',
      '5% Off + Tax-free',
      '5% Off + 免税劵',
      '5% Off + 면세劵',
    ),
    primaryCategory: COUPON_CATEGORY.shopping,
    secondaryCategory: COUPON_CATEGORY.jewelry,
    rating: '4.5',
    reviewCount: 31,
    participantCount: '200+',
    listImageFile: DEFAULT_COUPON_LIST_IMAGE_FILE,
    detailImageZhFile: '6411d5180b417f1cc767c28b6bfc1a4a.png',
    detailImageEnFile: 'f058a3ca8caf2ef8721576e1ebacadd3.png',
  },
])
